import { getMediaKindPolicy, type MediaKind, type PlatformMediaAsset } from '@aysar/contracts';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { type MediaPickerState } from '../types/media-field.types';
import { type MediaSource } from '../types/media-source.types';
import { useMediaAnnouncer } from '../useMediaAnnouncer/useMediaAnnouncer';
import { useMediaUpload } from '../useMediaUpload/useMediaUpload';

export interface UseMediaPickerLogicInput {
  value: string | null;
  kind: MediaKind;
  source: MediaSource;
  maxBytes?: number;
  isReadOnly?: boolean;
  isDisabled?: boolean;
  onValueChange: (value: string | null) => void;
}

export interface UseMediaPickerLogic {
  state: MediaPickerState;
  /** The resolved asset — `null` in every state but `ready`. */
  asset: PlatformMediaAsset | null;
  /** 0–100 while uploading, `null` when the transport cannot say. */
  progress: number | null;
  /** An i18n key in the `uiKit` namespace, or null. */
  errorKey: string | null;
  /** The `accept` attribute, from the kind registry rather than a literal. */
  accept: string;
  canUpload: boolean;
  canBrowse: boolean;
  canClear: boolean;
  /** True when the stored id resolved to nothing and a retry is worth offering. */
  retry: (() => void) | undefined;
  upload: (files: readonly File[] | FileList) => Promise<void>;
  cancelUpload: () => void;
  choose: (mediaId: string) => void;
  clear: () => void;
  announcement: string;
}

/**
 * Everything `MediaPicker` does, with no DOM in sight.
 *
 * Three decisions live here and are worth naming.
 *
 * **The picker keeps an id, never an asset.** An upload resolves with the
 * finalized row, and this throws all of it away except the id — the source's
 * `useAsset` then resolves the full record, exactly as it does for a value the
 * operator never touched. One path, so a just-uploaded logo and a
 * loaded-from-storage one are the same state with the same facts on screen,
 * rather than two code paths that drift.
 *
 * **A failed upload changes nothing.** The previous value survives, because a
 * failed *replacement* that cleared the field would make the operator hunt for
 * a logo that was already correct.
 *
 * **A cancel is not a failure.** `classifyUploadError` answers `null` for it, and
 * that `null` travels all the way to the UI as "show no message".
 */
export function useMediaPickerLogic({
  value,
  kind,
  source,
  maxBytes,
  isReadOnly = false,
  isDisabled = false,
  onValueChange,
}: UseMediaPickerLogicInput): UseMediaPickerLogic {
  const { t } = useTranslation('uiKit');
  const announcer = useMediaAnnouncer();
  const { say, announceReadOnlyRefusal } = announcer;

  const resolved = source.useAsset(value, kind);

  const isLocked = isDisabled || isReadOnly;

  // The transfer, the progress, the abort-on-unmount and the one line of copy
  // when it fails all live in the kit's shared upload machine — this control
  // supplies only the ROUTE and what to do with the finished asset.
  const uploader = useMediaUpload({
    run: (file, options) => source.upload({ file, kind, maxBytes, ...options }),
    onUploaded: (asset) => {
      onValueChange(asset.id);
    },
    announcer,
    isLocked,
  });
  const { errorKey, clearError } = uploader;

  const state: MediaPickerState = uploader.isUploading
    ? 'uploading'
    : value === null
      ? 'empty'
      : resolved.asset !== null
        ? 'ready'
        : resolved.isUnavailable
          ? 'unavailable'
          : 'resolving';

  const choose = useCallback(
    (mediaId: string) => {
      if (isLocked) {
        announceReadOnlyRefusal();
        return;
      }

      clearError();
      onValueChange(mediaId);
      say(t('media.announce.chosen'));
    },
    [announceReadOnlyRefusal, clearError, isLocked, onValueChange, say, t],
  );

  const clear = useCallback(() => {
    if (isLocked) {
      announceReadOnlyRefusal();
      return;
    }

    clearError();
    // `null`, never `''`: an empty string would be a uuid the schema has to make
    // an exception for, and "no logo" is genuinely the absence of a value.
    onValueChange(null);
    say(t('media.announce.removed'));
  }, [announceReadOnlyRefusal, clearError, isLocked, onValueChange, say, t]);

  const accept = useMemo(() => getMediaKindPolicy(kind).mimeTypes.join(','), [kind]);

  return {
    state,
    asset: resolved.asset,
    progress: uploader.progress,
    errorKey,
    accept,
    canUpload: !isLocked && !uploader.isUploading,
    canBrowse: !isLocked && !uploader.isUploading,
    canClear: !isLocked && !uploader.isUploading && value !== null,
    // Only where retrying could change the answer: a resolve that came back
    // empty might succeed on a transient failure, and an empty field has
    // nothing to retry.
    retry: state === 'unavailable' ? resolved.refetch : undefined,
    upload: uploader.start,
    cancelUpload: uploader.cancel,
    choose,
    clear,
    announcement: announcer.announcement,
  };
}
